import { useState } from "react";
import { INIT_REGISTRO_FORM, INIT_TOURNAMENT_FORM } from "../../constants";
import { useTournamentCRUD, useRegistroCRUD } from "../../hooks/useCRUD";
import { useForm, useModal } from "../../hooks/useForms";
import {
  deleteRegistroFromSupabase,
  deleteTournamentFromSupabase,
  saveRegistroToSupabase,
  saveTournamentToSupabase,
} from "../../services/supabaseData";
import {
  createRegistroValidator,
  validateTournamentForm,
} from "./completeValidators";

export default function useCompleteModeActions({
  tournaments,
  selectableTournaments = tournaments,
  setTournaments,
  registros,
  setRegistros,
  setDataStatus,
  getActiveUserId,
}) {
  const { getTournament, deleteTournament } = useTournamentCRUD(
    selectableTournaments,
    setTournaments,
    registros,
    setRegistros
  );

  const { deleteRegistro } = useRegistroCRUD(selectableTournaments, setRegistros);

  const tournamentForm = useForm(INIT_TOURNAMENT_FORM, validateTournamentForm);
  const tournamentModal = useModal();

  const registroForm = useForm(
    INIT_REGISTRO_FORM,
    createRegistroValidator(getTournament)
  );
  const registroModal = useModal();

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleSaveTournament = async () => {
    if (!tournamentForm.validate()) return;

    try {
      setDataStatus(null);
      const activeUserId = await getActiveUserId();
      const savedTournament = await saveTournamentToSupabase(
        activeUserId,
        tournamentForm.values,
        tournamentModal.editingId
      );
      setTournaments((prev) =>
        tournamentModal.editingId
          ? prev.map((t) => (t.id === savedTournament.id ? savedTournament : t))
          : [savedTournament, ...prev]
      );
      tournamentForm.reset();
      tournamentModal.close();
      setDataStatus({
        type: "success",
        message: tournamentModal.editingId ? "Torneio atualizado." : "Torneio salvo.",
      });
    } catch (error) {
      setDataStatus({
        type: "error",
        message: error.message || "Nao foi possivel salvar o torneio.",
      });
    }
  };

  const handleEditTournament = (tournament) => {
    tournamentForm.setValues({
      nome: tournament.nome,
      buyin: tournament.buyin,
      plataforma: tournament.plataforma || "",
      modalidade: tournament.modalidade || "Vanilla",
      players: tournament.players || "",
      valorTicket: tournament.valorTicket || "",
    });
    tournamentModal.open(tournament.id);
  };

  const handleDeleteTournament = async (id) => {
    try {
      setDataStatus(null);
      const activeUserId = await getActiveUserId();
      await deleteTournamentFromSupabase(activeUserId, id);
      deleteTournament(id);
      setDeleteTarget(null);
      setDataStatus({
        type: "success",
        message: "Torneio removido.",
      });
    } catch (error) {
      setDataStatus({
        type: "error",
        message: error.message || "Nao foi possivel remover o torneio.",
      });
    }
  };

  const handleSaveRegistro = async () => {
    if (!registroForm.validate()) return;

    try {
      setDataStatus(null);
      const activeUserId = await getActiveUserId();
      const tournament = getTournament(registroForm.values.torneioId);
      const savedRegistro = await saveRegistroToSupabase(
        activeUserId,
        registroForm.values,
        registroModal.editingId,
        tournament
      );
      setRegistros((prev) =>
        registroModal.editingId
          ? prev.map((r) => (r.id === savedRegistro.id ? savedRegistro : r))
          : [savedRegistro, ...prev]
      );
      registroForm.reset();
      registroModal.close();
      setDataStatus({
        type: "success",
        message: registroModal.editingId ? "Registro atualizado." : "Registro salvo.",
      });
    } catch (error) {
      setDataStatus({
        type: "error",
        message: error.message || "Nao foi possivel salvar o registro.",
      });
    }
  };

  const handleEditRegistro = (registro) => {
    const tournament = getTournament(registro.torneioId);
    registroForm.setValues({
      plataforma: registro.plataforma || tournament?.plataforma || "",
      torneioId: String(registro.torneioId),
      inscricoes: registro.inscricoes || "1",
      premiacao: registro.premiacao || "",
      tickets: registro.tickets || "",
      isSatellite: registro.isSatellite || false,
    });
    registroModal.open(registro.id);
  };

  const handleDeleteRegistro = async (id) => {
    try {
      setDataStatus(null);
      const activeUserId = await getActiveUserId();
      await deleteRegistroFromSupabase(activeUserId, id);
      deleteRegistro(id);
      setDeleteTarget(null);
      setDataStatus({
        type: "success",
        message: "Registro removido.",
      });
    } catch (error) {
      setDataStatus({
        type: "error",
        message: error.message || "Nao foi possivel remover o registro.",
      });
    }
  };

  return {
    tournamentForm,
    tournamentModal,
    registroForm,
    registroModal,
    getTournament,
    deleteTarget,
    setDeleteTarget,
    handleSaveTournament,
    handleEditTournament,
    handleDeleteTournament,
    handleSaveRegistro,
    handleEditRegistro,
    handleDeleteRegistro,
  };
}
